import { inject, Injectable } from '@angular/core';
import { catchError, forkJoin, from, map, mergeMap, of, switchMap } from 'rxjs';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { debounceTime, filter } from 'rxjs/operators';
import { RFIActions } from './rfis.actions';
import { NotificationV2Service } from '@app/notification-v2.service';
import { RFI, RFI_STATUS, RFIListingItem } from '@app/store/rfis/rfi.types';
import { InteractionBarStateService } from '@app/services/interaction-bar-state.service';
import { RfiApiService } from '@app/services/rfi-api.service';

import { RFISelectors } from '@app/store/rfis/rfis.selectors';
import { DriveApiService } from '@app/services/drive-api.service';
import { viewProjectSelectors } from '@app/store/view-project/view-project.selectors';
import { CurrentUserService } from '@app/services/current-user.service';
import { viewProjectActions } from '@app/store/view-project/view-project.actions';

@Injectable()
export class RfisEffects {
  private actions$ = inject(Actions);
  private rfiApiService = inject(RfiApiService);
  private notif = inject(NotificationV2Service);
  private interactionBarStateService = inject(InteractionBarStateService);
  private store = inject(Store);
  private driveApiService = inject(DriveApiService);
  private userService = inject(CurrentUserService);

  loadRFIs$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        viewProjectActions.refreshNeeded, // be aware this runs on every interaction bar close
        RFIActions.loadRFIs,
        RFIActions.filtersChanged,
        RFIActions.updateRFISuccess,
        RFIActions.createRFISuccess,
        RFIActions.onOpenRFIListing,
        RFIActions.deleteRFISuccess,
      ),
      concatLatestFrom(() => [
        this.store.select(RFISelectors.selectRFIState),
        this.store.select(viewProjectSelectors.selectProjectId),
      ]),
      switchMap(([action, state, projectId]) => {
        const filters =
          'filters' in action
            ? { ...state.filters, ...action.filters }
            : {
                ...state.filters,
                page: 1,
              };
        if ('projectId' in action) {
          projectId = action.projectId;
        }

        if (!projectId) {
          return of(RFIActions.cancel());
        }

        return this.rfiApiService.loadRfiLog(projectId, filters).pipe(
          map((data: RFIListingItem[]) => {
            return RFIActions.loadRFIsSuccess({ rfis: data, filters: filters });
          }),
          catchError((err) => {
            this.notif.showError(err?.error?.message ?? 'Failed to load RFIs');
            console.error(err);
            return of(
              RFIActions.loadRFIsFailure({
                error: err?.error?.message ?? err?.message ?? 'Unknown error',
              }),
            );
          }),
        );
      }),
    );
  });

  loadNextPage$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RFIActions.loadNextPage),
      debounceTime(200),
      concatLatestFrom(() => this.store.select(RFISelectors.selectRFIState)),
      map(([action, stateFilters]) => {
        if (stateFilters.lastPageEmpty) {
          return RFIActions.cancel();
        }
        const page = (stateFilters.filters.page ?? 1) + 1;
        return RFIActions.filtersChanged({ projectId: action.projectId, filters: { page } });
      }),
    );
  });

  loadNextRFINumber$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RFIActions.onCreateRFISidebarOpened),
      switchMap((action) => {
        return this.rfiApiService.getNextRFINumber(action.projectId);
      }),
      map((response) => {
        return RFIActions.loadRFINumberSuccess({ rfiNumber: response.next_id });
      }),
    );
  });

  loadProjectTeamMembers$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RFIActions.onOpenRFIListing),
      filter(() => !this.userService.isFreeSP),
      map((action) => {
        return viewProjectActions.loadProjectTeamMembers({ projectId: action.projectId });
      }),
    );
  });

  loadProjectInviteMembers$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RFIActions.onOpenRFIListing),
      filter(() => !this.userService.isFreeSP),
      map((action) => {
        return viewProjectActions.loadProjectInviteMembers({ projectId: action.projectId });
      }),
    );
  });

  loadRFIDetails$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RFIActions.onOpenRFIDetails),
      switchMap((action) => {
        if (action.rfiId === null) {
          return of(RFIActions.cancel());
        }
        const hash = this.notif.showLoading('Loading RFI details...');
        return this.rfiApiService.getRFIDetails(action.rfiId).pipe(
          map((rfi: RFI) => {
            this.notif.close(hash);
            return RFIActions.loadRFIDetailsSuccess({ rfi });
          }),
          catchError((err) => {
            this.notif.close(hash);
            this.notif.showError(err?.error?.message ?? 'Failed to load RFI details');
            return of(
              RFIActions.loadRFIsFailure({
                error: err?.error?.message ?? err?.message ?? 'Unknown error',
              }),
            );
          }),
        );
      }),
    );
  });

  setSeenStatus$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RFIActions.onOpenRFIDetails),
      concatLatestFrom(() => this.store.select(RFISelectors.selectAllRFIs)),
      switchMap(([action, allRFIs]) => {
        const selectedRFI = allRFIs.find((r) => r.id === action.rfiId);
        if (
          !selectedRFI ||
          selectedRFI.responsible_seen_at ||
          selectedRFI.rfi_responsible?.id !== this.userService.data?.id
        ) {
          return of(RFIActions.cancel());
        }

        return this.rfiApiService.setRFIResponsibleSeenStatus(action.rfiId).pipe(
          map((response) => {
            return RFIActions.updateRFIResponsibleSeenStatusSuccess({ rfi: response });
          }),
          catchError((err) => {
            console.error('Failed to set RFI seen status', err);
            return of(
              RFIActions.updateRFIResponsibleSeenStatusFailure({
                rfiId: action.rfiId,
                error: err?.error?.message ?? err?.message ?? 'Unknown error',
              }),
            );
          }),
        );
      }),
    );
  });

  createRFI$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RFIActions.onCreateRFI),
      mergeMap((action) => {
        const hash = this.notif.showLoading('Saving...');
        return this.rfiApiService.createRfi(action.data).pipe(
          map((rfi: any) => {
            this.notif.close(hash);
            this.notif.showSuccess?.('RFI created');
            if (action.closeSidebar) {
              this.interactionBarStateService.close();
            }
            return RFIActions.createRFISuccess({ rfi });
          }),
          catchError((err) => {
            this.notif.close(hash);
            this.notif.showError(err?.error?.message ?? 'Failed to create RFI');
            return of(
              RFIActions.createRFIFailure({
                error: err?.error?.message ?? err?.message ?? 'Unknown error',
              }),
            );
          }),
        );
      }),
    );
  });

  updateRFI$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RFIActions.updateRFI),
      switchMap((action) => {
        const deleteFileRequests = action?.filesToDelete?.map((fileId) =>
          from(this.driveApiService.deleteFilePromise(fileId)).pipe(
            catchError((err) => {
              console.error(err);
              this.notif.showError('Failed to delete a file from RFI');
              return of(null);
            }),
          ),
        );
        if (!deleteFileRequests?.length) {
          return of(action);
        }
        return forkJoin(deleteFileRequests).pipe(map((responses) => action));
      }),
      switchMap((action) => {
        return this.rfiApiService.updateRfi(action.rfiId, action.rfi).pipe(
          map((rfi: RFI) => {
            this.notif.showSuccess?.('RFI updated');
            if (action.closeSidebar) {
              this.interactionBarStateService.close();
            }
            return RFIActions.updateRFISuccess({ rfi });
          }),
          catchError((err) => {
            this.notif.showError(err?.error?.message ?? 'Failed to update RFI');
            return of(
              RFIActions.updateRFIFailure({
                rfiId: action.rfiId,
                error: err?.error?.message ?? err?.message ?? 'Unknown error',
              }),
            );
          }),
        );
      }),
    );
  });

  saveAnswer$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RFIActions.saveAnswer),
      map((action) => {
        return RFIActions.updateRFI({
          rfi: {
            answer: action.answerText,
            answer_status: action.status,
          },
          rfiId: action.rfiId,
          filesToDelete: action.filesToDelete ?? [],
        });
      }),
    );
  });

  saveQuestion$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RFIActions.saveQuestion),
      map((action) => {
        return RFIActions.updateRFI({
          rfi: {
            question: action.questionText,
          },
          rfiId: action.rfiId,
          filesToDelete: action.filesToDelete ?? [],
        });
      }),
    );
  });

  approveAnswer$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RFIActions.approveAnswer),
      map((action) => {
        return RFIActions.updateRFI({
          rfi: {
            answer_status: 'approved',
          },
          rfiId: action.rfiId,
          filesToDelete: [],
        });
      }),
    );
  });

  markRFIOpen$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RFIActions.markRFIOpen),
      map((action) => {
        return RFIActions.updateRFI({
          rfi: {
            status: RFI_STATUS.OPEN,
          },
          rfiId: action.rfiId,
          filesToDelete: [],
        });
      }),
    );
  });

  closeRFI$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RFIActions.closeRFI),
      map((action) => {
        return RFIActions.updateRFI({
          rfi: {
            status: RFI_STATUS.CLOSED,
          },
          rfiId: action.rfiId,
          filesToDelete: [],
        });
      }),
    );
  });

  reOpenRFI$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RFIActions.reOpenRFI),
      map((action) => {
        return RFIActions.updateRFI({
          rfi: {
            status: RFI_STATUS.OPEN,
          },
          rfiId: action.rfiId,
          filesToDelete: [],
        });
      }),
    );
  });

  voidRFI$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RFIActions.voidRFI),
      map((action) => {
        return RFIActions.updateRFI({
          rfi: {
            status: RFI_STATUS.VOID,
          },
          rfiId: action.rfiId,
          filesToDelete: [],
        });
      }),
    );
  });

  deleteRFI$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RFIActions.deleteRFI),
      switchMap((action) =>
        this.rfiApiService.deleteRfi(action.rfiId).pipe(
          map(() => {
            this.notif.showSuccess?.('RFI deleted');
            this.interactionBarStateService.close();
            return RFIActions.deleteRFISuccess({ rfiId: action.rfiId });
          }),
          catchError((err) => {
            this.notif.showError(err?.error?.message ?? 'Failed to delete RFI');
            return of(
              RFIActions.deleteRFIFailure({
                rfiId: action.rfiId,
                error: err?.error?.message ?? err?.message ?? 'Unknown error',
              }),
            );
          }),
        ),
      ),
    );
  });

  cleanUp$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(viewProjectActions.onDestroy),
      map(() => {
        return RFIActions.onProjectViewClosed();
      }),
    );
  });
}
